import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TargetEnvRadio } from "@/features/data/target-env-radio";
import { useDeleteEntity, useDeleteGroup } from "@/hooks/use-data";
import type { TargetEnv } from "@/lib/types";
import { useCallback, useState } from "react";

const MAX_PER_CLICK = 50;

/**
 * Called once per successful group-side delete. The Phase 5/Task 9 banner
 * registers two rows per delete (PROD + DEV) when both items_job_ids are
 * populated. Entities don't have items-sweep jobs so this is unused on the
 * entities path.
 */
export type BulkJobsCallback = (
  jobIds: { prod: string | null; dev: string | null },
  groupId: string,
  groupName: string,
) => void;

export interface DeleteSelectedDialogProps {
  selectedIds: string[];
  /** Display names aligned 1:1 with selectedIds; used in the confirmation
   *  preview AND passed back to the banner via onJobEnqueued. */
  selectedNames: string[];
  type: "groups" | "entities";
  /** Optional even with exactOptionalPropertyTypes — explicit `| undefined`
   *  so callers can spread `onJobEnqueued: undefined` from another optional. */
  onJobEnqueued?: BulkJobsCallback | undefined;
  onCleared: () => void;
}

interface FailureRecord {
  id: string;
  name: string;
  error: string;
}

export function DeleteSelectedDialog({
  selectedIds,
  selectedNames,
  type,
  onJobEnqueued,
  onCleared,
}: DeleteSelectedDialogProps) {
  const deleteGroup = useDeleteGroup();
  const deleteEntity = useDeleteEntity();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<"confirm" | "running" | "done">("confirm");
  const [progress, setProgress] = useState(0); // 0..ids.length
  const [succeeded, setSucceeded] = useState(0);
  const [failures, setFailures] = useState<FailureRecord[]>([]);
  // IR-661 Rotem reopen (2026-05-11): one env applies to the whole batch.
  // Required, no default — Confirm disabled until set.
  const [targetEnv, setTargetEnv] = useState<TargetEnv | null>(null);

  const cappedIds = selectedIds.slice(0, MAX_PER_CLICK);
  const cappedNames = selectedNames.slice(0, MAX_PER_CLICK);
  const overCap = selectedIds.length > MAX_PER_CLICK;

  const reset = useCallback(() => {
    setPhase("confirm");
    setProgress(0);
    setSucceeded(0);
    setFailures([]);
    setTargetEnv(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (targetEnv === null) return;
    setPhase("running");
    setProgress(0);
    setSucceeded(0);
    setFailures([]);

    let s = 0;
    const f: FailureRecord[] = [];
    for (let i = 0; i < cappedIds.length; i++) {
      const id = cappedIds[i];
      if (!id) continue; // satisfies noUncheckedIndexedAccess
      const name = cappedNames[i] ?? id;
      try {
        if (type === "groups") {
          const data = await deleteGroup.mutateAsync({ id, targetEnv });
          // Task 9 dual-env: register up to 2 banner rows. With env scoping
          // applied, prod-only/dev-only batches produce only one row per
          // group via the existing null-guard at the banner sink.
          if (onJobEnqueued) {
            onJobEnqueued(data.items_job_ids, id, name);
          }
        } else {
          await deleteEntity.mutateAsync({ id, targetEnv });
        }
        s += 1;
        setSucceeded(s);
      } catch (err) {
        f.push({
          id,
          name,
          error: err instanceof Error ? err.message : String(err),
        });
        setFailures([...f]);
        // Stop-on-first-error per locked decision.
        setProgress(i + 1);
        setPhase("done");
        return;
      }
      setProgress(i + 1);
    }
    setPhase("done");
  }, [cappedIds, cappedNames, type, onJobEnqueued, deleteGroup, deleteEntity, targetEnv]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (phase === "done" && succeeded > 0) {
      onCleared();
    }
    // Defer reset to the next open so the closing transition doesn't flash
    // "confirm" state to the user.
    setTimeout(reset, 200);
  }, [phase, succeeded, onCleared, reset]);

  const previewNames = cappedNames.slice(0, 10);
  const remaining = Math.max(0, cappedIds.length - previewNames.length);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) handleClose();
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="iris-btn iris-btn--primary text-xs"
          style={{
            background: "var(--iris-neon-pink)",
            color: "var(--iris-bg-0)",
            borderColor: "var(--iris-neon-pink)",
          }}
        >
          Delete Selected
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>
          Delete {cappedIds.length} {type}
        </DialogTitle>

        {phase === "confirm" && (
          <>
            <DialogDescription>
              You are about to soft-delete {cappedIds.length} {type}.
              {targetEnv === null ? (
                <> Pick a target environment to enable Delete.</>
              ) : targetEnv === "both" ? (
                <>
                  {" "}
                  Each {type === "groups" ? "group" : "entity"} will be deleted on{" "}
                  <strong>BOTH prod and dev</strong>
                  {type === "groups" ? " and will enqueue items-sweep jobs on each env." : "."}
                </>
              ) : (
                <>
                  {" "}
                  Targeting{" "}
                  <strong>{targetEnv === "prod" ? "production" : "development"} only</strong>.
                </>
              )}
              {overCap && (
                <>
                  {" "}
                  <strong>
                    Showing first {MAX_PER_CLICK} of {selectedIds.length}.
                  </strong>{" "}
                  Run again to delete the rest.
                </>
              )}
            </DialogDescription>
            <div style={{ marginTop: 12 }}>
              <TargetEnvRadio value={targetEnv} onChange={setTargetEnv} actionVerb="delete" />
            </div>
            <ul
              style={{
                marginTop: 12,
                maxHeight: 200,
                overflowY: "auto",
                fontSize: 11,
                color: "var(--iris-fg-3)",
                fontFamily: "var(--iris-font-mono)",
                paddingLeft: 0,
                listStyle: "none",
              }}
            >
              {previewNames.map((name, i) => (
                <li key={cappedIds[i] ?? `idx-${i}`} style={{ padding: "2px 0" }}>
                  • {name}
                </li>
              ))}
              {remaining > 0 && (
                <li style={{ padding: "2px 0", color: "var(--iris-fg-4)" }}>+{remaining} more…</li>
              )}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <DialogClose asChild>
                <button type="button" className="iris-btn text-xs">
                  Cancel
                </button>
              </DialogClose>
              <button
                type="button"
                className="iris-btn text-xs"
                style={{
                  background: "var(--iris-neon-pink)",
                  color: "var(--iris-bg-0)",
                  borderColor: "var(--iris-neon-pink)",
                }}
                onClick={handleConfirm}
                disabled={targetEnv === null}
              >
                Delete {cappedIds.length}
              </button>
            </div>
          </>
        )}

        {phase === "running" && (
          <>
            <DialogDescription>
              Deleting {progress} of {cappedIds.length}…
            </DialogDescription>
            <div
              style={{
                marginTop: 12,
                width: "100%",
                height: 6,
                background: "var(--iris-bg-2)",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${(progress / cappedIds.length) * 100}%`,
                  height: "100%",
                  background: "var(--iris-neon-cyan)",
                  transition: "width 200ms",
                }}
              />
            </div>
          </>
        )}

        {phase === "done" && (
          <>
            <DialogDescription>
              {succeeded} of {cappedIds.length} {type} deleted.
              {failures.length > 0 && (
                <>
                  {" "}
                  <strong>{failures.length} failed.</strong> Bulk loop stopped on first error per
                  safety policy; the remaining items were not attempted.
                </>
              )}
            </DialogDescription>
            {failures.length > 0 && (
              <ul
                style={{
                  marginTop: 12,
                  maxHeight: 200,
                  overflowY: "auto",
                  fontSize: 11,
                  color: "var(--iris-neon-pink)",
                  paddingLeft: 0,
                  listStyle: "none",
                }}
              >
                {failures.map((f) => (
                  <li key={f.id} style={{ padding: "2px 0" }}>
                    • {f.name}: {f.error}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="iris-btn iris-btn--primary text-xs"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
